import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Send, Users } from 'lucide-react';
import api from '../../../../utils/api';
import MainLayout from '../../../../components/layout/MainLayout';
import FilteredLeadSelectTable from '../../../../components/automation/FilteredLeadSelectTable';
import TemplateCard from '../../../../components/automation/TemplateCard';
import SchedulerPicker from '../../../../components/automation/SchedulerPicker';
import VariableMapper from '../../../../components/automation/VariableMapper';

// Templates will be fetched dynamically from the API

const BulkMessagePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [scheduleMode, setScheduleMode] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [templateVariables, setTemplateVariables] = useState([]);
  const [variableMappings, setVariableMappings] = useState([]);
  const [latestSelectedLeadData, setLatestSelectedLeadData] = useState(null);
  const [allLoadedLeads, setAllLoadedLeads] = useState([]);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [dynamicLeadFields, setDynamicLeadFields] = useState([]);

  React.useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const res = await api('/custom-fields');
        if (res.ok) {
          setCustomFieldDefs(await res.json());
        }
      } catch (e) {
        console.error('Failed to fetch custom fields:', e);
      }
    };
    fetchCustomFields();
  }, []);

  const buildLeadFields = (lead, customFields, allLeads) => {
    let standard = [];
    let leadCustomFields = [];
    const keysToIgnore = ['_id', '__v', 'customFields', 'logs', 'followUps', 'tenantId', 'addedBy', 'assignedTelecaller', 'assignedExecutive', 'assignedBy', 'updatedAt', 'createdAt', 'addedByName', 'assignedTelecallerName', 'assignedExecutiveName', 'assignedByName', 'updatedBy', 'updatedByName'];

    const extractKeys = (l) => {
      Object.keys(l).forEach(key => {
        if (!keysToIgnore.includes(key) && typeof l[key] !== 'object') {
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          if (!standard.find(s => s.value === key)) {
            standard.push({ value: key, label: label });
          }
        }
      });

      if (l.customFields && typeof l.customFields === 'object') {
        Object.keys(l.customFields).forEach(key => {
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
          if (!leadCustomFields.find(c => c.value === `customFields.${key}`)) {
            leadCustomFields.push({ value: `customFields.${key}`, label: `${label} (Custom)` });
          }
        });
      }
    };

    if (lead) extractKeys(lead);
    if (allLeads && allLeads.length > 0) {
      allLeads.forEach(extractKeys);
    }

    if (standard.length === 0) {
      standard = [
        { value: 'name', label: 'Name' },
        { value: 'phone', label: 'Phone' },
        { value: 'email', label: 'Email' },
        { value: 'location', label: 'Location' },
        { value: 'propertyInterest', label: 'Property Interest' },
        { value: 'budget', label: 'Budget' },
        { value: 'leadType', label: 'Lead Type' },
        { value: 'status', label: 'Status' },
        { value: 'source', label: 'Source' }
      ];
    }

    const defs = (customFields || []).map(cf => ({
      value: `customFields.${cf.name}`,
      label: cf.name + ' (Custom)'
    }));

    // Merge custom fields from definition and from lead object without duplicates
    const allCustom = [...leadCustomFields];
    defs.forEach(def => {
       if (!allCustom.find(c => c.value === def.value)) {
           allCustom.push(def);
       }
    });

    return [...standard, ...allCustom];
  };

  React.useEffect(() => {
    setDynamicLeadFields(buildLeadFields(latestSelectedLeadData, customFieldDefs, allLoadedLeads));
  }, [latestSelectedLeadData, customFieldDefs, allLoadedLeads]);

  // When a template is selected, parse variables
  React.useEffect(() => {
    if (selectedTemplate) {
      const vars = [];
      const regex = /\{\{([^}]+)\}\}/g;
      const addedPositions = new Set();

      (selectedTemplate.components || []).forEach(comp => {
         if (comp.type === 'BODY' && comp.text) {
             let match;
             while ((match = regex.exec(comp.text)) !== null) {
                 if (!addedPositions.has(`body_${match[1]}`)) {
                     vars.push({ position: match[1], componentType: 'body' });
                     addedPositions.add(`body_${match[1]}`);
                 }
             }
         } else if (comp.type === 'HEADER') {
             if (comp.format === 'TEXT' && comp.text) {
                 let match;
                 while ((match = regex.exec(comp.text)) !== null) {
                     if (!addedPositions.has(`header_${match[1]}`)) {
                         vars.push({ position: match[1], componentType: 'header' });
                         addedPositions.add(`header_${match[1]}`);
                     }
                 }
             } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(comp.format)) {
                 vars.push({ position: `header_${comp.format.toLowerCase()}`, componentType: 'header', format: comp.format.toLowerCase() });
             }
         } else if (comp.type === 'BUTTONS' && comp.buttons) {
             comp.buttons.forEach((btn, index) => {
                 let match;
                 if (btn.type === 'URL' && btn.url) {
                     while ((match = regex.exec(btn.url)) !== null) {
                         if (!addedPositions.has(`btn_${index}_${match[1]}`)) {
                             vars.push({ position: match[1], componentType: 'button', subType: 'url', index: String(index) });
                             addedPositions.add(`btn_${index}_${match[1]}`);
                         }
                     }
                 }
             });
         }
      });
      
      // If they are numbers, sort numerically, otherwise sort alphabetically
      vars.sort((a, b) => {
        if (!isNaN(a.position) && !isNaN(b.position)) return parseInt(a.position) - parseInt(b.position);
        return String(a.position).localeCompare(String(b.position));
      });
      setTemplateVariables(vars);
      
      // Initialize default mappings
      const initialMappings = vars.map(v => ({
        position: v.position,
        componentType: v.componentType,
        format: v.format,
        subType: v.subType,
        index: v.index,
        source: v.format ? 'static' : 'lead_field',
        leadField: (v.position === '1' || String(v.position).toLowerCase() === 'name') ? 'name' : '', // default to name if possible
        staticValue: ''
      }));
      setVariableMappings(initialMappings);
    } else {
      setTemplateVariables([]);
      setVariableMappings([]);
    }
  }, [selectedTemplate]);

  React.useEffect(() => {
    if (step === 2 && templates.length === 0) {
      fetchTemplates();
    }
  }, [step]);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    setTemplateError(null);
    try {
      const res = await api('/whatsapp/templates');
      if (!res.ok) throw new Error('Failed to fetch templates');
      const data = await res.json();
      
      const formatted = data.map(t => {
        const bodyComponent = t.components?.find(c => c.type === 'BODY');
        const headerComponent = t.components?.find(c => c.type === 'HEADER');
        return {
          id: t.id || t.name,
          name: t.name,
          category: t.category,
          language: t.language,
          body: bodyComponent ? bodyComponent.text : '',
          header: headerComponent ? headerComponent.text : '',
          status: t.status,
          parameter_format: t.parameter_format,
          components: t.components
        };
      }).filter(t => t.status === 'APPROVED');
      
      setTemplates(formatted);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplateError('Failed to load templates. Please make sure your WhatsApp API is configured correctly.');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const hasVariables = templateVariables.length > 0;
  const TOTAL_STEPS = hasVariables ? 4 : 3;

  const handleNext = () => {
    if (step === 1 && selectedLeads.length === 0) return alert('Please select at least one lead');
    if (step === 2 && !selectedTemplate) return alert('Please select a template');
    
    if (step === 2 && !hasVariables) {
      // Skip variable step if none exist
      setStep(TOTAL_STEPS);
    } else if (step === 3 && hasVariables && variableMappings.some(m => m.source === 'lead_field' && !m.leadField)) {
       return alert('Please select a lead field for all variables mapped from lead.');
    } else {
      setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (step === TOTAL_STEPS && !hasVariables) {
      setStep(2);
    } else {
      setStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (scheduleMode === 'schedule' && (!scheduledDate || !scheduledTime)) {
      return alert('Please select a date and time for scheduling');
    }

    setIsSubmitting(true);
    
    let scheduledAt = null;
    if (scheduleMode === 'schedule') {
      scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
    }

    try {
      const res = await api('/automation/bulk-send', {
        method: 'POST',
        body: {
          templateId: selectedTemplate.id,
          templateName: selectedTemplate.name,
          templateLanguage: selectedTemplate.language || 'en_US',
          parameterFormat: selectedTemplate.parameter_format || 'POSITIONAL',
          variableMappings,
          leadIds: selectedLeads,
          scheduledAt
        }
      });
      
      if (!res.ok) throw new Error('Failed to create job');

      alert('Bulk message job created successfully!');
      navigate('/automation/whatsapp-api/bulk/pending');
    } catch (error) {
      console.error('Error submitting job:', error);
      alert('Failed to submit job. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <button 
            onClick={() => navigate('/automation')}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              background: 'none', 
              border: 'none', 
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              padding: 0,
              fontSize: '14px'
            }}
          >
            <ArrowLeft size={16} /> Back to Hub
          </button>
          
          <button
            onClick={() => navigate('/automation/whatsapp-api/bulk/pending')}
            style={{
              padding: '8px 16px',
              background: 'white',
              border: '1px solid var(--color-border-primary)',
              borderRadius: '6px',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              fontWeight: '500'
            }}
          >
            View Pending Queue
          </button>
        </div>

        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>Send Bulk Messages</h1>
        <p style={{ color: 'var(--color-text-tertiary)', marginBottom: '32px' }}>Follow the steps to configure and send your broadcast.</p>

        {/* Step Indicator */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <div style={{ flex: 1, height: '4px', background: step >= 1 ? 'var(--color-brand-primary)' : 'var(--color-border-primary)', borderRadius: '2px' }} />
            
            <div style={{ flex: 1, height: '4px', background: step >= 2 ? 'var(--color-brand-primary)' : 'var(--color-border-primary)', borderRadius: '2px' }} />
            
            {hasVariables && (
              <div style={{ flex: 1, height: '4px', background: step >= 3 ? 'var(--color-brand-primary)' : 'var(--color-border-primary)', borderRadius: '2px' }} />
            )}

            <div style={{ flex: 1, height: '4px', background: step >= TOTAL_STEPS ? 'var(--color-brand-primary)' : 'var(--color-border-primary)', borderRadius: '2px' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: 'var(--color-text-secondary)', fontWeight: '500' }}>
            <span style={{ color: step >= 1 ? 'var(--color-brand-primary)' : 'inherit' }}>1. Select Leads</span>
            <span style={{ color: step >= 2 ? 'var(--color-brand-primary)' : 'inherit' }}>2. Choose Template</span>
            {hasVariables && (
              <span style={{ color: step >= 3 ? 'var(--color-brand-primary)' : 'inherit' }}>3. Variables</span>
            )}
            <span style={{ color: step >= TOTAL_STEPS ? 'var(--color-brand-primary)' : 'inherit' }}>{TOTAL_STEPS}. Schedule</span>
          </div>
        </div>

        {/* Step Content */}
        <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid var(--color-border-primary)', minHeight: '400px' }}>
          {step === 1 && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Select Target Audience</h3>
              <FilteredLeadSelectTable 
                selectedLeads={selectedLeads} 
                setSelectedLeads={setSelectedLeads} 
                onLeadDataChange={(lead, allLeads) => {
                  setLatestSelectedLeadData(lead);
                  setAllLoadedLeads(allLeads || []);
                }}
              />
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Choose Message Template</h3>
              {loadingTemplates ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Loading templates from WhatsApp API...</div>
              ) : templateError ? (
                <div style={{ padding: '20px', background: 'var(--color-background-error)', color: 'var(--color-text-error)', borderRadius: '8px' }}>
                  {templateError}
                </div>
              ) : templates.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>No approved templates found.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                  {templates.map(template => (
                    <TemplateCard 
                      key={template.id} 
                      template={template} 
                      isSelected={selectedTemplate?.id === template.id}
                      onSelect={setSelectedTemplate}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && hasVariables && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Configure Message Variables</h3>
              <p style={{ color: 'var(--color-text-secondary)', marginBottom: '24px', fontSize: '14px' }}>
                Your selected template contains variables. Map them to CRM fields or enter manual values to personalize each message.
              </p>
              
              <div style={{ maxWidth: '600px' }}>
                {templateVariables.map((v, idx) => (
                  <VariableMapper 
                    key={`${v.componentType}_${v.position}`}
                    variable={v}
                    mapping={variableMappings.find(m => m.position === v.position && m.componentType === v.componentType) || {}}
                    templateBody={selectedTemplate.body}
                    sampleLead={latestSelectedLeadData || { name: 'John Doe', phone: '+919876543210' }}
                    leadFields={dynamicLeadFields}
                    onUpdate={(newMapping) => {
                      const updated = [...variableMappings];
                      const idx = updated.findIndex(m => m.position === v.position && m.componentType === v.componentType);
                      if (idx !== -1) updated[idx] = newMapping;
                      setVariableMappings(updated);
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {step === TOTAL_STEPS && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>When should we send this?</h3>
              <div style={{ background: 'var(--color-background-info)', padding: '16px', borderRadius: '8px', marginBottom: '24px', color: 'var(--color-brand-primary)', display: 'flex', gap: '8px' }}>
                <Users size={20} />
                <span>You are about to send <b>{selectedTemplate?.name}</b> to <b>{selectedLeads.length}</b> leads.</span>
              </div>
              <SchedulerPicker 
                scheduleMode={scheduleMode} setScheduleMode={setScheduleMode}
                scheduledDate={scheduledDate} setScheduledDate={setScheduledDate}
                scheduledTime={scheduledTime} setScheduledTime={setScheduledTime}
              />
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
          <button 
            onClick={handleBack} 
            disabled={step === 1 || isSubmitting}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: '1px solid var(--color-border-primary)',
              background: 'white',
              cursor: step === 1 ? 'not-allowed' : 'pointer',
              opacity: step === 1 ? 0.5 : 1,
              fontWeight: '500'
            }}
          >
            Back
          </button>
          
          {step < TOTAL_STEPS ? (
            <button 
              onClick={handleNext}
              disabled={(step === 1 && selectedLeads.length === 0) || (step === 2 && !selectedTemplate)}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                background: ((step === 1 && selectedLeads.length === 0) || (step === 2 && !selectedTemplate)) ? 'var(--color-background-tertiary)' : 'var(--color-brand-primary)',
                color: ((step === 1 && selectedLeads.length === 0) || (step === 2 && !selectedTemplate)) ? 'var(--color-text-tertiary)' : 'white',
                cursor: ((step === 1 && selectedLeads.length === 0) || (step === 2 && !selectedTemplate)) ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              Next Step <ArrowRight size={18} />
            </button>
          ) : (
            <button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--color-brand-primary)',
                color: 'white',
                cursor: isSubmitting ? 'wait' : 'pointer',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isSubmitting ? 0.7 : 1
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Confirm & Send'} <Send size={16} />
            </button>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default BulkMessagePage;
